import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  ArrowRight,
  MessageSquare,
  Brain,
  Globe,
  Zap,
  BarChart3,
  Code,
  Headphones,
  Users,
  FileText,
  Info,
  Play,
  Share2,
} from 'lucide-react';

// Replace these with your YouTube video IDs once uploaded
const CHATBOT_WALKTHROUGH_VIDEO_ID = 'CHATBOT_WALKTHROUGH_VIDEO_ID';

const TIER_COLORS = [
  { bg: 'bg-purple-50', text: 'text-purple-900', sub: 'text-purple-500' },
  { bg: 'bg-secondary-600', text: 'text-white', sub: 'text-secondary-100' },
  { bg: 'bg-gray-800', text: 'text-white', sub: 'text-gray-300' },
];

const ChatbotPage = () => {
  const { t } = useTranslation();
  const [billingCycle, setBillingCycle] = useState('annual');
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [plansError, setPlansError] = useState(false);

  const transformPlan = (plan) => {
    const docsLabel = (plan.maxDocumentsPerChatbot === -1 || plan.maxDocumentsPerChatbot == null)
      ? t('chatbotPage.planFeatDocsUnlimited')
      : t('chatbotPage.planFeatDocs', { count: plan.maxDocumentsPerChatbot });

    let agentsLabel = null;
    if (plan.includedAgents == null) {
      agentsLabel = t('chatbotPage.planFeatAgentsUnlimited');
    } else if (plan.includedAgents > 0) {
      agentsLabel = plan.includedAgents === 1
        ? t('chatbotPage.planFeatAgents', { count: 1 })
        : t('chatbotPage.planFeatAgentsPlural', { count: plan.includedAgents });
    }

    return {
      name: plan.planName || plan.name || 'Plan',
      monthly: Number(plan.monthlyPrice) || 0,
      annual: Number(plan.annualPrice) || 0,
      description: plan.description || '',
      popular: plan.popular || false,
      docs: docsLabel,
      agents: agentsLabel,
      features: [
        plan.messagesPerMonth != null
          ? t('chatbotPage.planFeatMessages', { count: Number(plan.messagesPerMonth).toLocaleString() })
          : null,
        ...(Array.isArray(plan.features) ? plan.features : []),
      ].filter(Boolean),
      disclaimer: plan.overageEnabled && parseFloat(plan.pricePerAdditional1k) > 0
        ? t('pricing.extraMessagesRate', { price: plan.pricePerAdditional1k })
        : t('pricing.extraMessages'),
    };
  };

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await fetch('/api/subscriptions/chatbot/plans');
        if (!res.ok) throw new Error('Failed to fetch plans');
        const data = await res.json();
        const arr = Array.isArray(data) ? data : (data.content ?? []);
        setPlans(arr.map(transformPlan));
      } catch (err) {
        console.error('Failed to fetch chatbot pricing plans:', err);
        setPlansError(true);
      } finally {
        setLoadingPlans(false);
      }
    };
    fetchPlans();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- helper identity changes each render; dependency list is intentional
  }, []);

  const features = [
    { icon: Brain,        title: t('chatbotPage.f1Title'), description: t('chatbotPage.f1Desc') },
    { icon: Globe,        title: t('chatbotPage.f2Title'), description: t('chatbotPage.f2Desc') },
    { icon: Zap,          title: t('chatbotPage.f3Title'), description: t('chatbotPage.f3Desc') },
    { icon: BarChart3,    title: t('chatbotPage.f4Title'), description: t('chatbotPage.f4Desc') },
    { icon: Code,         title: t('chatbotPage.f5Title'), description: t('chatbotPage.f5Desc') },
    { icon: MessageSquare, title: t('chatbotPage.f6Title'), description: t('chatbotPage.f6Desc') },
  ];

  const benefits = [
    { stat: '24/7', label: t('chatbotPage.b1Label'), description: t('chatbotPage.b1Desc') },
    { stat: '80%', label: t('chatbotPage.b2Label'), description: t('chatbotPage.b2Desc') },
    { stat: '<2s', label: t('chatbotPage.b3Label'), description: t('chatbotPage.b3Desc') },
    { stat: '60%', label: t('chatbotPage.b4Label'), description: t('chatbotPage.b4Desc') },
  ];

  const steps = [
    { step: '1', title: t('chatbotPage.step1Title'), description: t('chatbotPage.step1Desc') },
    { step: '2', title: t('chatbotPage.step2Title'), description: t('chatbotPage.step2Desc') },
    { step: '3', title: t('chatbotPage.step3Title'), description: t('chatbotPage.step3Desc') },
  ];

  return (
    <>
      <Helmet>
        <title>AI Chatbot - Customer Support Automation | mwbTechs</title>
        <meta
          name="description"
          content="Deploy intelligent AI chatbots for 24/7 customer support. Train on your content, support multiple languages, and automate 80% of inquiries."
        />
      </Helmet>

      <div className="pt-20 min-h-screen bg-white">
        {/* Hero Section */}
        <section className="section-sm bg-gradient-to-br from-secondary-50 to-purple-50">
          <div className="container-custom">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="inline-flex items-center gap-2 bg-secondary-100 text-secondary-700 px-4 py-2 rounded-full mb-6">
                  <MessageSquare className="w-4 h-4" />
                  <span className="font-medium">{t('chatbotPage.badge')}</span>
                </div>
                <h1 className="text-5xl font-bold text-gray-900 mb-6">
                  {t('chatbotPage.heroTitle')}
                </h1>
                <p className="text-xl text-gray-600 mb-8">
                  {t('chatbotPage.heroSubtitle')}
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link to="/signup" className="btn btn-secondary text-lg">
                    {t('chatbotPage.tryItFree')}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                  <button
                    onClick={() => document.getElementById('chatbot-video-section').scrollIntoView({ behavior: 'smooth' })}
                    className="btn btn-outline text-lg flex items-center gap-2"
                  >
                    <Play className="w-5 h-5" />
                    {t('chatbotPage.watchWalkthrough')}
                  </button>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="relative"
              >
                <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-200">
                  {/* Chat Demo Preview */}
                  <div className="space-y-4">
                    <div className="flex gap-3">
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex-shrink-0 flex items-center justify-center text-lg">🤖</div>
                      <div className="bg-gray-100 rounded-2xl rounded-tl-none p-4 max-w-[80%]">
                        <p className="text-sm text-gray-700">Hi! How can I help you today?</p>
                      </div>
                    </div>
                    <div className="flex gap-3 justify-end">
                      <div className="bg-secondary-600 text-white rounded-2xl rounded-tr-none p-4 max-w-[80%]">
                        <p className="text-sm">I need help with a complex billing issue.</p>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-secondary-200 flex-shrink-0"></div>
                    </div>
                    <div className="flex gap-3">
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex-shrink-0 flex items-center justify-center text-lg">🤖</div>
                      <div className="bg-gray-100 rounded-2xl rounded-tl-none p-4 max-w-[80%]">
                        <p className="text-sm text-gray-700">I'm connecting you with a support agent. Please hold on.</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex-shrink-0 flex items-center justify-center">
                        <Headphones className="w-5 h-5 text-green-600" />
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded-2xl rounded-tl-none p-4 max-w-[80%]">
                        <p className="text-xs font-semibold text-green-700 mb-1">Live Agent · Sarah</p>
                        <p className="text-sm text-gray-700">Hi! I have the full conversation history. Let me help you right away.</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 flex items-center justify-between text-sm text-gray-500">
                    <span>⚡ Instant AI + Human support</span>
                    <span>🌍 50+ languages</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="section">
          <div className="container-custom">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('chatbotPage.benefitsTitle')}
              </h2>
            </div>

            <div className="grid md:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="text-6xl font-bold text-secondary-600 mb-2">{benefit.stat}</div>
                  <div className="text-xl font-semibold text-gray-900 mb-2">{benefit.label}</div>
                  <p className="text-gray-600">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="section bg-gray-50">
          <div className="container-custom">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('chatbotPage.featuresTitle')}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {t('chatbotPage.featuresSubtitle')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-lg transition-shadow"
                  >
                    <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-secondary-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Live Agent Handover Section */}
        <section className="section">
          <div className="container-custom">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {/* Left: visual */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="relative"
              >
                {/* Flow diagram */}
                <div className="bg-gradient-to-br from-secondary-50 to-purple-50 rounded-2xl p-8 border border-secondary-100">
                  {/* AI step */}
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <Brain className="w-6 h-6 text-secondary-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">AI Bot</p>
                      <p className="text-sm text-gray-600 mt-0.5">Handles routine queries 24/7 — instantly.</p>
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="flex items-center gap-3 mb-6 pl-6">
                    <div className="w-0.5 h-8 bg-gradient-to-b from-secondary-300 to-green-400 mx-auto" style={{ marginLeft: 18 }}></div>
                  </div>

                  {/* Handover trigger */}
                  <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6 text-sm text-amber-800 flex items-center gap-2">
                    <Zap className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    Complex issue detected — triggering handover…
                  </div>

                  {/* Agent step */}
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                      <Headphones className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Live Agent</p>
                      <p className="text-sm text-gray-600 mt-0.5">Joins with full conversation history. No repeat needed.</p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Right: copy */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full mb-6">
                  <Users className="w-4 h-4" />
                  <span className="font-medium">{t('chatbotPage.liveAgentBadge')}</span>
                </div>
                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                  {t('chatbotPage.liveAgentTitle')}
                </h2>
                <p className="text-lg text-gray-500 mb-4">
                  {t('chatbotPage.liveAgentSubtitle')}
                </p>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  {t('chatbotPage.liveAgentDesc')}
                </p>
                <ul className="space-y-3">
                  {[
                    t('chatbotPage.liveAgentB1'),
                    t('chatbotPage.liveAgentB2'),
                    t('chatbotPage.liveAgentB3'),
                    t('chatbotPage.liveAgentB4'),
                  ].map((benefit, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="section">
          <div className="container-custom">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('chatbotPage.stepsTitle')}
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {steps.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-secondary-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Walkthrough Video Section */}
        <section id="chatbot-video-section" className="section bg-gray-50">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <div className="inline-flex items-center gap-2 bg-secondary-100 text-secondary-700 px-4 py-2 rounded-full mb-6">
                <Play className="w-4 h-4" />
                <span className="font-medium">{t('chatbotPage.videoSectionBadge')}</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('chatbotPage.videoSectionTitle')}
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                {t('chatbotPage.videoSectionSubtitle')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="max-w-4xl mx-auto"
            >
              <div className="rounded-2xl overflow-hidden shadow-2xl border border-gray-200 bg-gray-900 aspect-video">
                <iframe
                  src={`https://www.youtube.com/embed/${CHATBOT_WALKTHROUGH_VIDEO_ID}?rel=0&modestbranding=1`}
                  title="mwbTechs Chatbot — Full Walkthrough"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
              <div className="text-center mt-8">
                <Link to="/signup" className="btn btn-secondary text-lg">
                  {t('chatbotPage.tryItFree')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="section bg-white">
          <div className="container-custom">
            <div className="text-center mb-10">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('chatbotPage.pricingTitle')}
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                {t('chatbotPage.pricingSubtitle')}
              </p>
              {/* Billing cycle toggle */}
              <div className="inline-flex items-center bg-white rounded-full p-1 shadow-sm">
                <button
                  onClick={() => setBillingCycle('monthly')}
                  className={`px-6 py-2 rounded-full font-medium transition-all ${
                    billingCycle === 'monthly'
                      ? 'bg-secondary-600 text-white shadow'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t('chatbotPage.monthly')}
                </button>
                <button
                  onClick={() => setBillingCycle('annual')}
                  className={`px-6 py-2 rounded-full font-medium transition-all ${
                    billingCycle === 'annual'
                      ? 'bg-secondary-600 text-white shadow'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t('chatbotPage.annual')}
                  <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                    {t('chatbotPage.save10')}
                  </span>
                </button>
              </div>
            </div>

            {loadingPlans ? (
              <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                {[1, 2, 3].map(i => (
                  <div key={i} className="pricing-card animate-pulse">
                    <div className="h-6 bg-gray-200 rounded w-1/2 mb-4" />
                    <div className="h-10 bg-gray-200 rounded w-3/4 mb-6" />
                    <div className="space-y-3">
                      {[1, 2, 3, 4, 5].map(j => (
                        <div key={j} className="h-4 bg-gray-100 rounded w-full" />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : plansError ? (
              <div className="text-center py-16">
                <p className="text-gray-500 text-lg">{t('chatbotPage.contactPricing')}</p>
                <Link to="/contact" className="text-secondary-600 underline mt-2 inline-block">
                  {t('chatbotPage.getInTouch')}
                </Link>
              </div>
            ) : (
              <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                {plans.map((plan, index) => {
                  const colors = TIER_COLORS[index] ?? TIER_COLORS[0];
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className={`pricing-card overflow-hidden ${index === 1 ? 'pricing-card-featured' : ''}`}
                    >
                      {/* Colored header strip */}
                      <div className={`-mx-8 -mt-8 px-8 pt-8 pb-5 text-center mb-6 ${colors.bg}`}>
                        {index === 1 && (
                          <span className="inline-block text-xs font-semibold bg-white/25 text-white px-3 py-1 rounded-full mb-3 uppercase tracking-wider">
                            {t('chatbotPage.mostPopular')}
                          </span>
                        )}
                        <h3 className={`text-2xl font-bold ${colors.text}`}>{plan.name}</h3>
                        {plan.description && (
                          <p className={`text-sm mt-1 ${colors.sub}`}>{plan.description}</p>
                        )}
                      </div>

                      {/* Price */}
                      <div className="text-center mb-6">
                        {plan.monthly === 0 ? (
                          <div className="flex items-baseline justify-center gap-2">
                            <span className="text-4xl font-bold text-gray-900">{t('chatbotPage.custom')}</span>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-baseline justify-center gap-2">
                              <span className="text-5xl font-bold text-gray-900">
                                ${billingCycle === 'monthly' ? plan.monthly : Math.round(plan.annual / 12)}
                              </span>
                              <span className="text-gray-600">{t('chatbotPage.perMonth')}</span>
                            </div>
                            {billingCycle === 'annual' && (
                              <p className="text-sm text-green-600 mt-1">${plan.annual} {t('chatbotPage.billedAnnually')}</p>
                            )}
                          </>
                        )}
                      </div>

                      {/* Docs, Agents & Channels specs */}
                      <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 mb-5 space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <FileText className="w-4 h-4 text-secondary-500 flex-shrink-0" />
                          <span className="text-gray-700">{plan.docs}</span>
                        </div>
                        {plan.agents && (
                          <div className="flex items-center gap-2 text-sm">
                            <Users className="w-4 h-4 text-secondary-500 flex-shrink-0" />
                            <span className="text-gray-700">{plan.agents}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-sm">
                          <Share2 className="w-4 h-4 text-secondary-500 flex-shrink-0" />
                          <span className="text-gray-700">{t('chatbotPage.planFeatChannels')}</span>
                        </div>
                      </div>

                      <ul className="space-y-3 mb-4">
                        {plan.features.map((feature, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>

                      {plan.disclaimer && (
                        <p className="flex items-start gap-1.5 text-xs text-gray-500 italic mb-6">
                          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gray-400" />
                          {plan.disclaimer}
                        </p>
                      )}

                      <Link
                        to="/signup"
                        className={`w-full btn ${index === 1 ? 'btn-secondary' : 'btn-outline'}`}
                      >
                        {t('chatbotPage.getStarted')}
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="section-sm bg-gradient-to-r from-secondary-600 to-purple-600 text-white">
          <div className="container-custom text-center">
            <h2 className="text-4xl font-bold mb-4">
              {t('chatbotPage.ctaTitle')}
            </h2>
            <p className="text-xl text-purple-100 mb-8">
              {t('chatbotPage.ctaSubtitle')}
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/signup" className="btn btn-white text-lg">
                {t('chatbotPage.startFreeTrial')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <button
                onClick={() => document.getElementById('chatbot-video-section').scrollIntoView({ behavior: 'smooth' })}
                className="btn btn-outline text-white border-white hover:bg-white hover:text-secondary-600 text-lg flex items-center gap-2"
              >
                <Play className="w-5 h-5" />
                {t('chatbotPage.watchWalkthrough')}
              </button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ChatbotPage;
